import { useAuthReady } from './useAuthReady';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchIncptTransfers,
  resolveIncptRecipient,
  sendIncptTransfer,
} from '../lib/incpt';

export function useIncptTransfers() {
  const { canFetch: ready, initData, cacheUserId: userId } = useAuthReady();
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['incptTransfers', userId],
    enabled: ready,
    queryFn: () => fetchIncptTransfers(initData!),
    staleTime: 15_000,
  });

  const send = useMutation({
    mutationFn: (args: {
      toTgUserId: string;
      amount: string;
      idempotencyKey: string;
      reauthToken?: string;
    }) =>
      sendIncptTransfer(
        initData!,
        { toTgUserId: args.toTgUserId, amount: args.amount },
        { idempotencyKey: args.idempotencyKey, reauthToken: args.reauthToken },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['incptTransfers'] });
      void qc.invalidateQueries({ queryKey: ['presale'] });
    },
  });

  return {
    transfers: list.data?.transfers ?? [],
    loading: list.isLoading,
    refresh: () => list.refetch(),
    send,
  };
}

export function useResolveIncptRecipient(
  to: { tgUserId: string } | { username: string } | null,
) {
  const { canFetch, initData, cacheUserId: userId } = useAuthReady();
  const key =
    to == null
      ? ''
      : 'tgUserId' in to
        ? `id:${to.tgUserId}`
        : `user:${to.username}`;
  const ready = canFetch && !!to;

  const query = useQuery({
    queryKey: ['incptResolvedRecipient', userId, key],
    enabled: ready,
    queryFn: () => resolveIncptRecipient(initData!, to!),
    staleTime: 60_000,
    retry: false,
  });

  return {
    recipient: query.data ?? null,
    resolving: query.isFetching,
    failed: query.isError,
    notFound:
      query.error instanceof Error && query.error.message.includes('user_not_found'),
  };
}
